/**
 * AIResponseModal Component
 *
 * Modal popup that shows AI-generated analysis, explanations,
 * or code generation results.
 */

import { useState, useEffect } from 'react'
import { X, Sparkles, Copy, Check, RefreshCw, Code2, Loader2, Send, Save, AlertCircle } from 'lucide-react'
import Editor from '@monaco-editor/react'
import DraggableModal from './DraggableModal'
import './AIResponseModal.css'

function AIResponseModal({
  isOpen,
  onClose,
  title = 'AI-Powered Code Analysis',
  mode = 'explain', // 'explain' | 'generate' | 'optimize' | 'custom'
  selectedCode = '',
  sectionName = '',
  aiResponse = null,
  aiLoading = false,
  onRegenerate,
  onAccept,
  onCustomPromptSubmit,  // callback for custom prompt submission
  customPromptValue = '', // controlled custom prompt value
  onSave,                 // optional: (content) => Promise<void> — triggers Save button
  saving = false,         // true while save is in flight
  saveStatus = null,      // null | 'saved' | 'error'
}) {
  const [copied, setCopied] = useState(false)
  const [customPrompt, setCustomPrompt] = useState(customPromptValue)

  // Reset custom prompt when modal opens
  useEffect(() => {
    if (isOpen && mode === 'custom') {
      setCustomPrompt(customPromptValue || '')
    }
  }, [isOpen, mode, customPromptValue])

  if (!isOpen) return null

  // Handle custom prompt submission
  const handleCustomPromptSubmit = () => {
    if (customPrompt.trim() && onCustomPromptSubmit) {
      onCustomPromptSubmit(customPrompt.trim())
    }
  }

  // Handle Enter key in custom prompt input
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleCustomPromptSubmit()
    }
  }

  const handleCopy = () => {
    const textToCopy = mode === 'generate' ? aiResponse?.code : aiResponse?.explanation || aiResponse?.response
    if (textToCopy) {
      navigator.clipboard.writeText(textToCopy)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  const handleAccept = () => {
    if (aiResponse?.code) {
      onAccept?.(aiResponse.code)
    }
    onClose()
  }

  return (
    <div className="ai-modal-overlay" onClick={onClose}>
      <DraggableModal className="ai-modal" onClose={onClose} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="ai-modal-header">
          <div className="ai-modal-title">
            <Sparkles size={20} />
            <span>{title}</span>
          </div>
          <button className="ai-modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="ai-modal-content">
          {/* Section Info */}
          {sectionName && (
            <div className="ai-modal-section-info">
              <span className="section-label">Section:</span>
              <span className="section-name">{sectionName}</span>
            </div>
          )}

          {/* Custom Prompt Input (for custom mode, before response) */}
          {mode === 'custom' && !aiLoading && !aiResponse && (
            <div className="ai-custom-prompt-input">
              <label>Ask anything about this code:</label>
              <div className="prompt-input-wrapper">
                <textarea
                  value={customPrompt}
                  onChange={(e) => setCustomPrompt(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="E.g., What does this function do? How can I optimize this? What are the potential bugs?"
                  rows={3}
                  autoFocus
                />
                <button
                  className="prompt-submit-btn"
                  onClick={handleCustomPromptSubmit}
                  disabled={!customPrompt.trim()}
                >
                  <Send size={18} />
                </button>
              </div>
            </div>
          )}

          {/* Loading State */}
          {aiLoading && (
            <div className="ai-modal-loading">
              <Loader2 size={32} className="spinning" />
              <p>AI is analyzing the code...</p>
              <span>This may take a few seconds</span>
            </div>
          )}

          {/* Response Content */}
          {!aiLoading && aiResponse && (
            <div className="ai-modal-response">
              {/* Explanation Mode */}
              {(mode === 'explain' || mode === 'custom') && (
                <div className="ai-explanation">
                  <pre>{aiResponse.explanation || aiResponse.response}</pre>
                </div>
              )}

              {/* Generate Mode */}
              {mode === 'generate' && aiResponse.code && (
                <div className="ai-generated-code">
                  <div className="code-header">
                    <Code2 size={16} />
                    <span>Generated Java Code</span>
                    {aiResponse.confidence && (
                      <span className="confidence-badge">
                        {Math.round(aiResponse.confidence * 100)}% confidence
                      </span>
                    )}
                  </div>
                  <div className="code-editor-wrapper">
                    <Editor
                      height="300px"
                      language="java"
                      theme="vs-dark"
                      value={aiResponse.code}
                      options={{
                        readOnly: true,
                        minimap: { enabled: false },
                        fontSize: 13,
                        lineNumbers: 'on',
                        scrollBeyondLastLine: false,
                        wordWrap: 'on',
                        automaticLayout: true,
                        padding: { top: 12, bottom: 12 }
                      }}
                    />
                  </div>
                </div>
              )}

              {/* Optimize Mode */}
              {mode === 'optimize' && aiResponse.suggestions && (
                <div className="ai-suggestions">
                  <h4>Optimization Suggestions</h4>
                  <ul>
                    {aiResponse.suggestions.map((suggestion, index) => (
                      <li key={index}>{suggestion}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Selected Code Preview */}
          {selectedCode && !aiLoading && !aiResponse && (
            <div className="ai-selected-code">
              <div className="code-header">
                <Code2 size={16} />
                <span>Selected Code</span>
              </div>
              <pre>{selectedCode}</pre>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="ai-modal-footer">
          <button className="ai-modal-btn secondary" onClick={handleCopy} disabled={!aiResponse}>
            {copied ? <Check size={16} /> : <Copy size={16} />}
            {copied ? 'Copied!' : 'Copy to Clipboard'}
          </button>

          <div className="ai-modal-footer-right">
            {onRegenerate && (
              <button className="ai-modal-btn secondary" onClick={onRegenerate} disabled={aiLoading}>
                <RefreshCw size={16} />
                Regenerate
              </button>
            )}

            {/* Accept & Insert — only shown when caller provides an onAccept handler.
                Analyze contexts pass onAccept={null} to suppress this button,
                since there is no editor surface to insert code into.           */}
            {mode === 'generate' && aiResponse?.code && onAccept && (
              <button className="ai-modal-btn primary" onClick={handleAccept}>
                <Check size={16} />
                Accept & Insert
              </button>
            )}

            {/* Save button — only shown when onSave prop is provided and there's a response */}
            {onSave && aiResponse && !aiLoading && (() => {
              const contentToSave = mode === 'generate'
                ? aiResponse?.code
                : aiResponse?.explanation || aiResponse?.response
              if (!contentToSave) return null
              return (
                <button
                  className={`ai-modal-btn ${saveStatus === 'saved' ? 'primary' : saveStatus === 'error' ? 'danger' : 'secondary'}`}
                  onClick={() => onSave(contentToSave)}
                  disabled={saving || saveStatus === 'saved'}
                  title="Save AI output to project workspace"
                >
                  {saving ? (
                    <><Loader2 size={16} className="spinning" /> Saving…</>
                  ) : saveStatus === 'saved' ? (
                    <><Check size={16} /> Saved!</>
                  ) : saveStatus === 'error' ? (
                    <><AlertCircle size={16} /> Save Failed</>
                  ) : (
                    <><Save size={16} /> Save Output</>
                  )}
                </button>
              )
            })()}

            <button className="ai-modal-btn" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </DraggableModal>
    </div>
  )
}

export default AIResponseModal
